/**
 * @file AuthContext.tsx
 * @brief Application-wide authentication state + actions.
 *
 * @details
 * - Hydrates from `tokenStorage` on mount: if a token exists we hit
 *   `GET /api/auth/me` to confirm validity and fetch the user record.
 * - Exposes `login`, `logout`, and `refreshUser` actions plus the current
 *   `user`, `entitlements`, and `status` flag.
 * - Listens for the `verblizr:auth:expired` event broadcast by the Axios
 *   interceptor in `client.ts` so that any 401 anywhere in the app
 *   triggers a clean global logout.
 * - Entitlements come from `/me` (or `/entitlements` after login) so the
 *   employee Boardroom space can gate on `boardroom` without the
 *   admin-only `/api/company/features` route (28-07-2026).
 * - Two-step verification (B31, 25-10-2026): every sign-in sends the
 *   "trust this device" token if one is kept, and a 403
 *   TWO_FACTOR_REQUIRED / TWO_FACTOR_SETUP_REQUIRED sets
 *   `twoFactorChallenge` (memory only) for the code or setup-only screen.
 *
 * @dependencies
 * - shared/api/client       (HTTP transport)
 * - shared/api/endpoints    (auth route paths)
 * - shared/api/tokenStorage (JWT persistence)
 * - shared/types/api        (Zod schemas + types)
 *
 * @references
 * - BACKEND/src/features/auth/routes.js (POST /login, GET /me, POST /logout)
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  apiPost,
  apiGet,
  AUTH_EXPIRED_EVENT,
} from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { queryClient } from "@/shared/api/queryClient";
import { tokenStorage } from "@/shared/api/tokenStorage";
import { isSignedOutAnswer } from "./signedOutAnswer";
import { runSignIn } from "./signInFlow";
import {
  BackupCodesSchema,
  challengeFromError,
  parseSetupInfo,
} from "@/features/auth/twoFactor/twoFactorApi";
import {
  readTrustedDevice,
  saveTrustedDevice,
  trustedDeviceLabel,
  type TwoFactorChallenge,
} from "@/features/auth/twoFactor/twoFactorRules";
import {
  ALL_ENTITLEMENTS_ON,
  EntitlementsResponseSchema,
  LoginResponseSchema,
  MeResponseSchema,
  type AuthUser,
  type Entitlements,
} from "@/shared/types/api";
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type AppleLoginBody,
  type LoginCredentials,
  type SignInOptions,
  type TwoFactorProof,
  type TwoFactorSetupInfo,
} from "./auth-context";

/** A sign-in body plus the kept "trust this device" token, if any (B31). */
function withTrustedDevice<B extends object>(body: B): B & { trustedDeviceToken?: string } {
  const t = readTrustedDevice();
  return t ? { ...body, trustedDeviceToken: t } : body;
}

function isDesktopApp(): boolean {
  return typeof window !== "undefined" && Boolean((window as unknown as { verblizrDesktop?: unknown }).verblizrDesktop);
}

async function fetchEntitlements(): Promise<Entitlements> {
  try {
    const raw = await apiGet<unknown>(authEndpoints.entitlements);
    return EntitlementsResponseSchema.parse(raw).entitlements;
  } catch {
    // Fail-open — matches backend getEffectiveAccess on error.
    return { ...ALL_ENTITLEMENTS_ON };
  }
}

/**
 * The page a one-tap link answer names (`redirect`): /magic/verify, and after
 * the code /2fa/verify or setup-token /setup/confirm (B33). Callers still
 * check it with `safeMagicRedirect` before navigating.
 */
function answerRedirect(raw: unknown): string | null {
  const r = raw && typeof raw === "object" ? (raw as { redirect?: unknown }).redirect : null;
  return typeof r === "string" && r ? r : null;
}

// ----------------------------------------------------------------------
// Provider
// ----------------------------------------------------------------------
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlements>({
    ...ALL_ENTITLEMENTS_ON,
  });
  const [token, setToken] = useState<string | null>(() => tokenStorage.get());
  const [status, setStatus] = useState<AuthStatus>("idle");
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallenge | null>(null);
  const challengeRef = useRef<TwoFactorChallenge | null>(null);
  challengeRef.current = twoFactorChallenge;
  /** A failed sign-in that asked for the second step: keep its token. */
  const noteChallenge = useCallback((err: unknown) => {
    const c = challengeFromError(err);
    if (c) setTwoFactorChallenge(c);
    return c;
  }, []);

  const clearAuth = useCallback(() => {
    // ⛔ Drop every cached query with the session (01-10-2026). Nothing did
    // before: in the desktop app, signing out and in as someone else without
    // a reload showed the previous account's cached Home (meetings, notes,
    // scheduled sessions) until each query went stale.
    queryClient.clear();
    tokenStorage.clear();
    setUser(null);
    setToken(null);
    setEntitlements({ ...ALL_ENTITLEMENTS_ON });
    setStatus("unauthenticated");
  }, []);

  // ----------------------------------------------------------------------
  // refreshUser
  // Hits /api/auth/me with the currently stored token. On success, parses
  // and stores the user + entitlements.
  //
  // Only a real "you are not signed in" answer signs out (21-10-2026, DES):
  // a 401, or a 404 (the account is gone). A backend restart, a 5xx, a
  // timeout or no network used to clear the token too, so a reload during a
  // deploy or a dev-server restart dropped the user on the login page and
  // lost what they were doing. Now the token stays: an already loaded user
  // keeps working, and a cold start shows "Reconnecting…" and retries
  // (1s, 2s, 4s … up to 30s) until the server answers. The server is the
  // authority, so keeping a token that turns out stale grants nothing.
  // ----------------------------------------------------------------------
  const userRef = useRef<AuthUser | null>(null);
  userRef.current = user;
  const retryRef = useRef<{ timer: number | null; attempt: number }>({ timer: null, attempt: 0 });
  const refreshUserRef = useRef<() => Promise<AuthUser | null>>(async () => null);

  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    if (retryRef.current.timer != null) {
      window.clearTimeout(retryRef.current.timer);
      retryRef.current.timer = null;
    }
    const stored = tokenStorage.get();
    if (!stored) {
      setStatus("unauthenticated");
      setUser(null);
      setToken(null);
      setEntitlements({ ...ALL_ENTITLEMENTS_ON });
      return null;
    }

    // A user already on screen stays on screen while this runs.
    if (!userRef.current) setStatus((cur) => (cur === "reconnecting" ? cur : "loading"));
    try {
      const data = await apiGet<unknown>(authEndpoints.me);
      const parsed = MeResponseSchema.parse(data);
      retryRef.current.attempt = 0;
      setUser(parsed.user);
      setToken(stored);
      setEntitlements(parsed.entitlements ?? (await fetchEntitlements()));
      setStatus("authenticated");
      return parsed.user;
    } catch (err) {
      if (isSignedOutAnswer(err)) {
        retryRef.current.attempt = 0;
        tokenStorage.clear();
        setUser(null);
        setToken(null);
        setEntitlements({ ...ALL_ENTITLEMENTS_ON });
        setStatus("unauthenticated");
        return null;
      }
      console.warn(
        "[Auth] /me unreachable, keeping the session:",
        err instanceof Error ? err.message : err,
      );
      setToken(stored);
      if (userRef.current) {
        // Mid-session (Terms, Profile): nothing to undo, try again later.
        setStatus("authenticated");
        return userRef.current;
      }
      setStatus("reconnecting");
      const attempt = retryRef.current.attempt++;
      const delay = Math.min(30_000, 1000 * 2 ** attempt);
      retryRef.current.timer = window.setTimeout(() => {
        retryRef.current.timer = null;
        void refreshUserRef.current();
      }, delay);
      return null;
    }
  }, []);
  refreshUserRef.current = refreshUser;

  // Back online, or the tab comes back: reconnect now instead of waiting.
  useEffect(() => {
    if (status !== "reconnecting") return;
    const now = () => void refreshUserRef.current();
    const onVisible = () => {
      if (document.visibilityState === "visible") now();
    };
    window.addEventListener("online", now);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", now);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status]);

  useEffect(
    () => () => {
      if (retryRef.current.timer != null) window.clearTimeout(retryRef.current.timer);
    },
    [],
  );

  // ----------------------------------------------------------------------
  // login
  // POST /api/auth/login → { token, user }. Persists token, loads entitlements.
  // ----------------------------------------------------------------------
  // The panel loader starts HERE, once the server has said yes (W27,
  // 26-10-2026). It used to start before the first step was sent, so an
  // account with two-step verification saw "Signing you in…" (held 2.2s by
  // the guards) before the code screen, as if already in. `quiet`: a re-auth
  // from a delete screen, which must stay mounted.
  const establishSession = useCallback(async (raw: unknown, quiet = false): Promise<AuthUser> => {
    const parsed = LoginResponseSchema.parse(raw);
    if (!quiet) setStatus("loading");
    // A new sign-in starts with an empty cache, whoever was here before.
    queryClient.clear();
    tokenStorage.set(parsed.token);
    setToken(parsed.token);
    setUser(parsed.user);
    setEntitlements(await fetchEntitlements());
    setStatus("authenticated");
    // The login replies carry no Terms answer (`needsTermsAcceptance`), so read
    // /me once, quietly: no loader, and a failure changes nothing (06-10-2026).
    void apiGet<unknown>(authEndpoints.me)
      .then((data) => {
        const me = MeResponseSchema.safeParse(data);
        if (me.success && me.data.user.id === parsed.user.id) setUser(me.data.user);
      })
      .catch(() => {});
    return parsed.user;
  }, []);

  // Password, Google, Apple and Microsoft share one path (B31): the kept
  // trust token goes with the body, and a 2FA answer becomes the challenge.
  // No loader while the first step runs (W27): the page stays as it is, its
  // pressed button disabled, and a 2FA answer swaps in the code screen.
  // `reauth` (B33, 26-10-2026): signing in again from a delete screen. No
  // loader even on success (it unmounted the screen and lost the form), and
  // a failed or 2FA answer keeps the current session, so the code step
  // shows inside the delete screen.
  const signIn = useCallback(
    async (url: string, body: object, opts?: SignInOptions): Promise<AuthUser> => {
      const reauth = opts?.reauth === true;
      setTwoFactorChallenge(null);
      return runSignIn({
        send: () => apiPost<unknown>(url, withTrustedDevice(body)),
        openPanel: (raw) => establishSession(raw, reauth),
        onRefused: (err) => {
          noteChallenge(err);
          if (!reauth) clearAuth();
        },
      });
    },
    [clearAuth, establishSession, noteChallenge],
  );

  const login = useCallback(
    (credentials: LoginCredentials) => signIn(authEndpoints.login, credentials),
    [signIn],
  );
  const loginWithGoogle = useCallback(
    (idToken: string, opts?: SignInOptions) => signIn(authEndpoints.google, { idToken }, opts),
    [signIn],
  );
  const loginWithApple = useCallback(
    (body: AppleLoginBody, opts?: SignInOptions) => signIn(authEndpoints.apple, body, opts),
    [signIn],
  );
  const loginWithMicrosoft = useCallback(
    (idToken: string, opts?: SignInOptions) => signIn(authEndpoints.microsoft, { idToken }, opts),
    [signIn],
  );

  // ----------------------------------------------------------------------
  // Two-step verification: the second step of a sign-in (B31)
  // ----------------------------------------------------------------------
  const cancelTwoFactor = useCallback(() => setTwoFactorChallenge(null), []);

  const verifyTwoFactor = useCallback(
    async (
      proof: TwoFactorProof,
    ): Promise<{ user: AuthUser; backupCodesLeft: number | null; redirect: string | null }> => {
      const c = challengeRef.current;
      if (!c || c.kind !== "code") throw new Error("This sign-in has expired. Please sign in again.");
      const trust = proof.trustDevice === true && c.trustDeviceAllowed;
      const raw = await apiPost<{
        trustedDeviceToken?: unknown;
        trustedDeviceExpiresAt?: unknown;
        twoFactor?: { backupCodesLeft?: unknown };
        redirect?: unknown;
      }>(authEndpoints.twoFactorVerify, {
        pendingToken: c.pendingToken,
        ...(proof.backupCode ? { backupCode: proof.backupCode } : { code: proof.code }),
        trustDevice: trust,
        ...(trust ? { deviceLabel: trustedDeviceLabel(navigator.userAgent, isDesktopApp()) } : {}),
      });
      if (typeof raw?.trustedDeviceToken === "string") {
        saveTrustedDevice(
          raw.trustedDeviceToken,
          typeof raw.trustedDeviceExpiresAt === "string" ? raw.trustedDeviceExpiresAt : null,
        );
      }
      const left = raw?.twoFactor?.backupCodesLeft;
      const user = await establishSession(raw);
      setTwoFactorChallenge(null);
      return { user, backupCodesLeft: typeof left === "number" ? left : null, redirect: answerRedirect(raw) };
    },
    [establishSession],
  );

  /** The setup token as the bearer: it is not a session (B31). */
  const setupAuth = useCallback((): { headers: { Authorization: string } } => {
    const c = challengeRef.current;
    if (!c || c.kind !== "setup") throw new Error("This sign-in has expired. Please sign in again.");
    return { headers: { Authorization: `Bearer ${c.setupToken}` } };
  }, []);

  const startChallengeSetup = useCallback(async (): Promise<TwoFactorSetupInfo> => {
    return parseSetupInfo(await apiPost<unknown>(authEndpoints.twoFactorSetupStart, {}, setupAuth()));
  }, [setupAuth]);

  const confirmChallengeSetup = useCallback(
    async (
      code: string,
    ): Promise<{ backupCodes: string[]; redirect: string | null; finish: () => Promise<AuthUser> }> => {
      const raw = await apiPost<unknown>(authEndpoints.twoFactorSetupConfirm, { code }, setupAuth());
      const { backupCodes } = BackupCodesSchema.parse(raw);
      return {
        backupCodes,
        redirect: answerRedirect(raw),
        finish: async () => {
          const user = await establishSession(raw);
          setTwoFactorChallenge(null);
          return user;
        },
      };
    },
    [establishSession, setupAuth],
  );

  // ----------------------------------------------------------------------
  // logout
  // ----------------------------------------------------------------------
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiPost(authEndpoints.logout, {});
    } catch {
      // ignore — token may already be invalid; we still clear local state
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const handler = () => {
      // DEV only: a /__preview page runs with a stand-in user and no token,
      // so its requests answer 401 by design. Clearing the cache there made
      // every query ask again, 401 again and clear again, in a loop (11-10-
      // 2026, thousands of requests a minute). Production builds never skip.
      if (import.meta.env.DEV && window.location.pathname.startsWith("/__preview")) return;
      clearAuth();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, [clearAuth]);

  const loginWithMagicLink = useCallback(
    async (magicToken: string): Promise<{ user: AuthUser; redirect: string | null }> => {
      // An expired link must not sign out whoever is already signed in here.
      const hadSession = status === "authenticated" && !!tokenStorage.get();
      setTwoFactorChallenge(null);
      return runSignIn({
        send: () => apiPost<unknown>(authEndpoints.magicVerify, withTrustedDevice({ token: magicToken })),
        openPanel: async (raw) => ({ user: await establishSession(raw), redirect: answerRedirect(raw) }),
        onRefused: (err) => {
          // B31: a 2FA answer leaves the page on the code screen.
          noteChallenge(err);
          if (!hadSession) clearAuth();
        },
      });
    },
    [clearAuth, establishSession, noteChallenge, status],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      entitlements,
      token,
      login,
      loginWithGoogle,
      loginWithApple,
      loginWithMicrosoft,
      loginWithMagicLink,
      logout,
      refreshUser,
      twoFactorChallenge,
      cancelTwoFactor,
      verifyTwoFactor,
      startChallengeSetup,
      confirmChallengeSetup,
    }),
    [
      status, user, entitlements, token, login, loginWithGoogle, loginWithApple, loginWithMicrosoft,
      loginWithMagicLink, logout, refreshUser, twoFactorChallenge, cancelTwoFactor, verifyTwoFactor,
      startChallengeSetup, confirmChallengeSetup,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
